import { Avatar, Box, TableCell, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ITableHeadings, ITableMetadata } from '../../common/interfaces/common.interface';
import { initialMetadataState } from '../../common/constants/common.constants';
import BasicTable from '../../components/BasicTable/BasicTable';
import NoDataTableRow from '../../components/NoDataTableRow/NoDataTableRow';
import { KindergartenDisplayDto, KindergartenDto } from '../../common/interfaces/kindergarten.interface';
import TableHeader from '../../components/TableHeader';
import { getAdminKindergartens } from '../../api/kindergarten.api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { palette } from '../../theme';
export const KINDERGARTEN_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.logo', width: '10%' },
  { id: 2, value: '', label: 'common.name', width: '15%' },
  { id: 3, value: '', label: 'common.location', width: '75%' },
  { id: 4, value: '', label: 'common.status', width: '10%' }
];

const Kindergartens = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState('');
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);
  const [kindergartenList, setKindergartenList] = useState<KindergartenDisplayDto[]>();

  const navigate = useNavigate();

  useEffect(() => {
    fetchKindergartens();
  }, [metadata.currentPage, searchParams]);

  const fetchKindergartens = async () => {
    try {
      const data = await getAdminKindergartens(metadata.currentPage, metadata.itemsPerPage, searchParams);

      if (!data.results) {
        setKindergartenList([]);
        return;
      }
      const mapped: KindergartenDisplayDto[] = data.results.map((item: KindergartenDto) => ({
        ...item,
        location: item.locations?.map(loc => loc.name).join(', ') || ''
      }));

      setKindergartenList(mapped);
      if (data.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: data.paginationInfo?.totalItems ?? 0,
          totalPages: data.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      console.error('Failed to fetch kindergartens', error);
    }
  };

  const handleSearchChange = (searchParams: string) => {
    setMetadata(prevState => ({ ...prevState, currentPage: 1 }));
    setSearchParams(searchParams);
  };

  const handleRowClick = (id: string) => {
    navigate(`/${id}/details`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 7 }}>
      <Typography variant="h1">{t('nav.kindergartens')}</Typography>
      <Box
        sx={{
          border: `1px solid ${palette.line}`,
          borderRadius: 4
        }}
      >
        <TableHeader
          searchTitle={t('common.searchByName')}
          sortOptions={[]}
          setSearchValue={searchParams => handleSearchChange(searchParams)}
        />
        <BasicTable headings={KINDERGARTEN_TABLE_HEADINGS} loader={!kindergartenList} metadata={metadata} setMetadata={setMetadata}>
          {kindergartenList && kindergartenList.length > 0 ? (
            kindergartenList.map((item, index) => (
              <TableRow
                key={index}
                onClick={() => {
                  handleRowClick(item.id);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Avatar src={item.logo} alt={t('common.logo')} />
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.location}</TableCell>
                <TableCell>{t(`enums.generalStatus.${item.status}`)}</TableCell>
              </TableRow>
            ))
          ) : (
            <NoDataTableRow message={t('common.noResults')} collSpan={5} />
          )}
        </BasicTable>
      </Box>
    </Box>
  );
};

export default Kindergartens;
